/**
 * Class description
 *
 * Created: 2019-08-13
 * @author jonas
 */

import {BookOfShadowsGameModuleConfig} from "../../BookOfShadowsGameModuleConfig";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {Timeline, TimelineLite} from "gsap";
import {SymbolUtil} from "../../utils/SymbolUtil";
import {StateMatrix} from "../../utils/StateMatrix";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {BookOfShadowsParsedGameData} from "../../server/data/BookOfShadowsParsedGameData";
import {BookOfShadowsGameConfig, IBookOfShadowsWinRatioConfig} from "../../BookOfShadowsGameConfig";
import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SoundManager} from "../../sound/SoundManager";
import {BookOfShadows} from "../../BookOfShadows";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";

export class InitialWin implements GameRoundPresentation {
    private _gameData: BookOfShadowsParsedGameData;
    private _symbolStates: StateMatrix<string>;
    private _tl: TimelineLite;
    private _canPlayCountUp: boolean;

    /**
     * @inheritDoc
     * @param data
     */
    public prepare(data: BookOfShadowsParsedGameData): void {
        BookOfShadowsGameModuleConfig.countUpView.calculateTextScale(0, data.singleWin);

        this._gameData = data;
        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetLineWinData[]>this._gameData.betWins!,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);

        this._canPlayCountUp = (data.betWins.length > 0 && data.singleWin > 0 && !data.isBigWin && (data.scatterWins.length === 0));

        // #331
        const bet: number = (BookOfShadowsGameConfig.instance.TOTAL_COST ? BookOfShadowsGameConfig.instance.TOTAL_COST : data.playedBetValue);
        BookOfShadowsGameConfig.instance.WIN_BELOW_STAKE = (BookOfShadows.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= bet));
    }

    /**
     * @inheritDoc
     */
    public getAnimation(): TimelineLite | null {
        if (this._gameData.singleWin <= 0) {
            return null;
        }

        this._tl = new TimelineLite();
        const noWinAnimations: TimelineLite = this.getNoWinSymbolAnimations();
        const winAnimations: TimelineLite = this.getWinSymbolAnimations();

        let duration: number = 0,
            countStart: TimelineLite = new TimelineLite();
        if (this._canPlayCountUp) {
            duration = this._gameData.currentWinRatioConfig!.duration;

            // #331
            if (BookOfShadowsGameConfig.instance.WIN_BELOW_STAKE) {
                duration = 0;
            }
            countStart = BookOfShadowsGameModuleConfig.countUpView.getWinStepAnimation(0, this._gameData.singleWin, duration, false);
        }

        if (this._gameData.isBigWin || this._gameData.scatterWins.length > 0) {
            this._tl.add([
                noWinAnimations,
                winAnimations
            ], 0);
        }

        // #368
        const displayIndividualWinsInGame: boolean = BookOfShadows.api.gameClientConfiguration.displayIndividualWinsInGame;
        if (displayIndividualWinsInGame) {
            this._tl.add(BookOfShadowsGameModuleConfig.betLineWinAmountView.showBetlineAmountInitialWin(this._gameData.betWins));
        }

        this._tl.add([
            countStart,
            () => {
                if (this._canPlayCountUp) {
                    BookOfShadowsGameModuleConfig.countUpView.playAnimOnNormalWin();

                    if (duration > 1) {
                        BookOfShadowsGameModuleConfig.miniCoinShower.playMiniShower();
                    }
                }

                if (this._gameData.betWins.length > 0) {
                    if (this._gameData.isBigWin) {
                        SoundManager.playBigWinInitialWin();
                    } else if (this._gameData.scatterWins.length === 0) {
                        // #331
                        if (!BookOfShadowsGameConfig.instance.WIN_BELOW_STAKE) {
                            SoundManager.playWin((this._gameData.currentWinRatioConfig! as IBookOfShadowsWinRatioConfig).sound!);
                        }
                    }

                    // #268
                    if (this._gameData.scatterWins.length > 0) {
                        SoundManager.playFSBetlineHighlight();
                    }
                }
            }
        ], displayIndividualWinsInGame ? this._tl.totalDuration() : 0);

        return this._tl;
    }

    /**
     * @inheritDoc
     * @param isActive
     */
    public abort(isActive: boolean): gsap.SimpleTimeline | gsap.TimelineLite | gsap.TimelineMax | null {
        const tl: TimelineLite = new TimelineLite();

        // #368
        StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChildren();

        tl.add([
            this.getNoWinSymbolAnimations(),
            () => {
                BookOfShadowsGameModuleConfig.symWinAnims.stopAllAnimation();

                for (let reelId = 0; reelId < BookOfShadowsGameConfig.instance.REELS_NUM; reelId++) {
                    const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);
                    for (let coords of reelCoords) {
                        this.changeStateOfSymbols(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.normal!);
                    }
                }
            }]);

        return tl;
    }

    /**
     * @description This method will set the no win state.
     */
    private getNoWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            for (let reelId = 0; reelId < BookOfShadowsGameConfig.instance.REELS_NUM; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!.keyword);
                for (let coords of reelCoords) {
                    this.changeStateOfSymbols(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!);
                }
            }
        }, 0);

        return tl;
    }

    /**
     * @description This method will set the win state.
     */
    private getWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        for (let reelId = 0; reelId < BookOfShadowsGameConfig.instance.REELS_NUM; reelId++) {
            const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);
            const reelTL: TimelineLite = new TimelineLite();
            for (let coords of reelCoords) {
                reelTL.add(this.getSymbolAnimation(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.win), 0);
                reelTL.add(this.changeStateOfSymbols(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.normal));
            }
            tl.add(reelTL, 0.1);
        }

        return tl;
    }

    /**
     * @description This method will set the provided state of the provided symbol of any provided reel.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private getSymbolAnimation(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            const symbolAnimation: Timeline = BookOfShadowsGameModuleConfig.symWinAnims.playAnimation(reelId, symbolId, symbol, false);

            tl.add(symbolAnimation);
        }
        return tl;
    }

    /**
     * @description To change the state of symbols.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private changeStateOfSymbols(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            // #189
            if (state.keyword === "noWin" && symbol.symName === "S") {
                return tl;
            }

            // in FS when there is a win on fsSpecial symbol in betline win and it has to go back to normal state.
            if (state.keyword === "normal" && BookOfShadowsGameConfig.instance.FS_SPECIAL_SYMBOL === symbol.symName) {
                state = BookOfShadowsGameConfig.instance.SYMBOL_STATES.goldNormal;
            }

            const symbolAnimation: Timeline = symbol.changeState({
                state: state,
                setStack: true,
                fadeInDuration: 0.1
            })!;

            tl.add(symbolAnimation);
        }

        return tl;
    }
}